import { Component, OnInit, inject, ChangeDetectionStrategy, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ProcessDocumentService } from '../../services/process-document.service';
import { RequirementsListComponent } from '../../components/requirements-list/requirements-list.component';
import { AppSettings } from '@services/settings/app-settings.service';
import { CatalogService } from '@services/data/catalog.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-process-document-detail',
  standalone: true,
  imports: [CommonModule, RequirementsListComponent, PageHeaderComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './process-document-detail.component.html'
})
export class ProcessDocumentDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly processService = inject(ProcessDocumentService);
  readonly catalogService = inject(CatalogService);
  readonly appSettings = inject(AppSettings);

  processSlug = '';
  selectedProcess: any = null;
  requirements: any[] = [];
  private appliedSlug: string | null = null;

  constructor() {
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      this.processSlug = String(params.get('processSlug') || '').trim();
      this.resolveProcess();
    });

    effect(() => {
      this.catalogService.processes();
      this.catalogService.documentRequirements();
      this.catalogService.documentTypes();
      if (this.processSlug) this.resolveProcess();
      if (this.appliedSlug) {
        this.requirements = this.catalogService.getRequirementsByProcess(this.appliedSlug);
      }
    });
  }

  ngOnInit(): void {
    this.catalogService.loadCatalogs();
    if (this.catalogService.loaded()) {
      this.catalogService.loadDocumentRequirements().subscribe({
        next: (list) => {
          this.catalogService.documentRequirements.set(Array.isArray(list) ? list : []);
          this.resolveProcess();
        },
        error: (err) => console.error('Error cargando requisitos de documentos', err)
      });
    }
  }

  closeModule(): void {
    void this.router.navigate(['/configuracion/documentos']);
  }

  private resolveProcess(): void {
    if (!this.processSlug) {
      this.selectedProcess = null;
      this.requirements = [];
      this.appliedSlug = null;
      return;
    }
    const found = (this.catalogService.processes() ?? []).find(
      (p) => String(p.slug ?? p.id).toUpperCase() === this.processSlug.toUpperCase()
    );
    if (!found) return;
    this.selectedProcess = found;
    this.loadRequirements(found.slug);
  }

  private loadRequirements(processSlug: string): void {
    if (!processSlug) return;
    this.appliedSlug = processSlug;
    this.requirements = this.catalogService.getRequirementsByProcess(processSlug);
  }

  openManageRequirements(): void {
    const slug = this.selectedProcess?.slug;
    if (!slug) return;
    void this.router.navigate(['/configuracion/documentos', slug, 'gestionar']);
  }

  syncProcessRequirements(): void {
    if (!this.selectedProcess) return;

    Swal.fire({
      title: 'Sincronizando Requisitos',
      text: 'Espere por favor mientras se sincronizan los documentos...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    this.processService.syncRequirements(this.selectedProcess.slug).subscribe({
      next: (res) => {
        Swal.fire({
          icon: 'success',
          title: 'Sincronización Completada',
          text: [
            `Abiertas: +${res.insertedSlots ?? res.synchronizedRecords ?? 0} / -${res.removedSlots || 0} slot(s).`,
            res.removedDocuments ? `Archivos borrados: ${res.removedDocuments}.` : null,
            res.movedToObserved ? `Movidas a Observada: ${res.movedToObserved}.` : null,
            res.renewals ? `DOC_RENEWAL: ${res.renewals} solicitud(es), ${res.renewalSlots || 0} slot(s).` : null
          ].filter(Boolean).join(' '),
          confirmButtonText: 'Entendido'
        });
      },
      error: (err) => {
        Swal.fire('Error', 'No se pudo completar la sincronización: ' + (err.error?.message ?? err.message), 'error');
      }
    });
  }
}
